import { Alert, Stack, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useLocalState } from "../../../utils/hooks/global/useLocalStorage";
import CustomButton from "../../global/CustomButton";

// Renames a channel or changes its chat ID, e.g. after a Telegram group was
// upgraded to a supergroup and got a new ID. Only these two fields are
// sent, so the linked entity and subscriptions stay as they are.
const AdminEditChannelForm = ({ channel, channels, onSubmit }) => {
  const [jwt] = useLocalState(null, "jwt");
  const [name, setName] = useState(channel.name ?? "");
  const [chatId, setChatId] = useState(channel.chatId ?? "");
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const trimmedChatId = chatId.trim();
  const duplicate = channels?.find(
    (other) => other.id !== channel.id && other.chatId === trimmedChatId
  );
  const unchanged =
    name.trim() === (channel.name ?? "") && trimmedChatId === channel.chatId;
  const invalid = !name.trim() || !trimmedChatId || !!duplicate;

  const handleSave = async () => {
    setSaving(true);
    setErrorMessage("");
    try {
      const response = await fetch(`${backendUrl}/channels/${channel.id}`, {
        method: "PUT",
        credentials: "include",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          Authorization: "Bearer " + jwt,
        },
        body: JSON.stringify({
          name: name.trim(),
          chatId: trimmedChatId,
          // A Telegram access hash belongs to one chat. Without it the
          // backend looks the new chat up the next time it is used.
          ...(channel.channelType === "telegram" &&
          trimmedChatId !== channel.chatId
            ? { accessHash: null }
            : {}),
        }),
      });
      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(
          data.error || data.message || `Request failed (${response.status})`
        );
      }
      onSubmit();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        Edit channel
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {channel.channelType === "telegram" ? "Telegram" : "Slack"} channel
        {channel.baseEntity?.slug ? ` for ${channel.baseEntity.slug}` : ""}.
        Change the chat ID when the chat was recreated or upgraded and got a
        new ID; the linked entity and subscriptions stay the same.
      </Typography>
      <TextField
        label="Name"
        value={name}
        onChange={(e) => setName(e.target.value)}
        fullWidth
        required
      />
      <TextField
        label="Chat ID"
        value={chatId}
        onChange={(e) => setChatId(e.target.value)}
        fullWidth
        required
        error={!!duplicate}
        helperText={
          duplicate
            ? `Already used by "${duplicate.name}"`
            : channel.channelType === "telegram"
            ? "IDs of Telegram supergroups and channels start with -100"
            : undefined
        }
        InputProps={{ sx: { fontFamily: "monospace" } }}
      />
      {errorMessage && <Alert severity="error">{errorMessage}</Alert>}
      <Stack direction="row" spacing={1} justifyContent="flex-end">
        <CustomButton
          buttonText={saving ? "Saving…" : "Save"}
          color="black"
          disabled={saving || invalid || unchanged}
          onClick={handleSave}
        />
      </Stack>
    </Stack>
  );
};

export default AdminEditChannelForm;
